class NewsletterModal {
  constructor() {
    this.modal = document.getElementById('newsletter-modal-overlay');
    this.closeBtn = document.getElementById('newsletter-modal-close');
    this.form = this.modal?.querySelector('form');
    this.successMessage = document.getElementById('newsletter-success');

    if (!this.modal) return;

    this.settings = this.getSettings();

    // If modal is disabled, don't initialize
    if (!this.settings.enabled) return;

    this.hasShown = this.getStorageValue('newsletter-modal-shown');
    this.hasSubscribed = this.getStorageValue('newsletter-subscribed');

    this.init();
  }

  getSettings() {
    const modal = this.modal;
    return {
      enabled: modal.dataset.enabled === 'true',
      showDelay: parseInt(modal.dataset.showDelay || '10') * 1000,
      showOnExit: modal.dataset.showOnExit === 'true',
      showOnScroll: modal.dataset.showOnScroll === 'true'
    };
  }

  getStorageValue(key) {
    try {
      return localStorage.getItem(key) === 'true';
    } catch (e) {
      return false;
    }
  }

  setStorageValue(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      // localStorage not available
    }
  }

  init() {
    this.bindEvents();
    this.scheduleShow();

    // Add scroll listener if enabled
    if (this.settings.showOnScroll) {
      this.bindScrollTrigger();
    }
  }

  bindEvents() {
    // Close button
    this.closeBtn?.addEventListener('click', () => this.hideModal());

    // Click outside to close
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.hideModal();
      }
    });

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('active')) {
        this.hideModal();
      }
    });

    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }

    // Exit intent
    if (this.settings.showOnExit) {
      this.bindExitIntent();
    }
  }

  bindExitIntent() {
    let exitIntentTriggered = false;

    document.addEventListener('mouseleave', (e) => {
      if (e.clientY <= 0 && !exitIntentTriggered && this.shouldShow()) {
        exitIntentTriggered = true;
        this.showModal();
      }
    });
  }

  bindScrollTrigger() {
    let scrollTriggered = false;

    window.addEventListener('scroll', () => {
      if (scrollTriggered || !this.shouldShow()) return;

      const scrollPercentage = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;

      if (scrollPercentage >= 70) {
        scrollTriggered = true;
        this.showModal();
      }
    });
  }

  scheduleShow() {
    if (this.settings.showDelay > 0 && this.shouldShow()) {
      setTimeout(() => {
        if (this.shouldShow()) {
          this.showModal();
        }
      }, this.settings.showDelay);
    }
  }

  shouldShow() {
    return !this.hasShown && !this.hasSubscribed;
  }

  showModal() {
    if (!this.shouldShow()) return;

    this.modal.classList.add('active');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Focus trap
    const focusableElements = this.modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }

    this.setStorageValue('newsletter-modal-shown', 'true');
    this.hasShown = true;
  }

  hideModal() {
    this.modal.classList.remove('active');
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  async handleSubmit(e) {
    e.preventDefault();

    const formData = new FormData(this.form);
    const email = formData.get('contact[email]');

    if (!email || !this.isValidEmail(email)) {
      this.showError('有効なメールアドレスを入力してください。');
      return;
    }

    this.form.classList.add('submitting');

    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        this.showSuccess();
        this.setStorageValue('newsletter-subscribed', 'true');
        this.hasSubscribed = true;

        // Auto-close after success
        setTimeout(() => {
          this.hideModal();
        }, 3000);
      } else {
        throw new Error('Registration failed');
      }
    } catch (error) {
      this.showError('登録中にエラーが発生しました。もう一度お試しください。');
    }

    this.form.classList.remove('submitting');
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  showSuccess() {
    this.form.style.display = 'none';
    this.successMessage.style.display = 'block';
  }

  showError(message) {
    // Remove existing error messages
    const existingError = this.modal.querySelector('.newsletter-modal__error');
    if (existingError) {
      existingError.remove();
    }

    // Create and show error message
    const errorDiv = document.createElement('div');
    errorDiv.className = 'newsletter-modal__error';
    errorDiv.style.cssText = `
      color: #d32f2f;
      font-size: 1.4rem;
      margin-top: 1rem;
      text-align: center;
      padding: 1rem;
      background: rgba(211, 47, 47, 0.1);
      border-radius: var(--border-radius);
    `;
    errorDiv.textContent = message;

    this.form.appendChild(errorDiv);

    // Remove error after 5 seconds
    setTimeout(() => {
      errorDiv.remove();
    }, 5000);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new NewsletterModal();
});